import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  ArrowRight,
  Wrench,
  Wand2,
  Users,
  MessageSquare,
  Inbox,
  Clock,
  Globe,
  FileText,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { API_URL } from "../utils/api";

export default function AdminDashboard({ user }) {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState({
    activeClients: 0,
    pendingDrafts: 0,
    pendingSuggestions: 0,
    weekConversations: 0,
    topPendingClients: [],
    pendingDraftsList: [],
  });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();

      const [clientsR, draftsR, optClientsR] = await Promise.allSettled([
        fetch(`${API_URL}/api/admin/handyman/list`, { headers }),
        fetch(`${API_URL}/api/admin/onboarding/drafts?status=trial_requested`, { headers }),
        fetch(`${API_URL}/api/admin/optimization/clients`, { headers }),
      ]);

      const next = { ...data };

      if (clientsR.status === "fulfilled" && clientsR.value.ok) {
        const j = await clientsR.value.json();
        const list = j.handymen || j.clients || [];
        next.activeClients = list.filter((c) => c.active !== false).length;
      }

      if (draftsR.status === "fulfilled" && draftsR.value.ok) {
        const j = await draftsR.value.json();
        const drafts = j.drafts || [];
        next.pendingDrafts = drafts.length;
        next.pendingDraftsList = drafts.slice(0, 5);
      }

      if (optClientsR.status === "fulfilled" && optClientsR.value.ok) {
        const j = await optClientsR.value.json();
        const clients = j.clients || [];
        next.pendingSuggestions = clients.reduce((s, c) => s + (c.pending_suggestions || 0), 0);
        next.weekConversations = clients.reduce((s, c) => s + (c.recent_conversations || 0), 0);
        next.topPendingClients = clients
          .filter((c) => (c.pending_suggestions || 0) > 0)
          .sort((a, b) => (b.pending_suggestions || 0) - (a.pending_suggestions || 0))
          .slice(0, 5);
      }

      setData(next);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const fmtDate = (s) => {
    if (!s) return "—";
    return new Date(s).toLocaleDateString("sv-SE", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Welcome */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Välkommen tillbaka{user?.fullName ? `, ${user.fullName.split(" ")[0]}` : ""}!
        </h1>
        <p className="mt-1 text-gray-600">
          Översikt över era AI-kundmottagare och vad som väntar på er åtgärd.
        </p>
      </div>

      {/* Stat cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard
          label="Aktiva klienter"
          value={data.activeClients}
          icon={Wrench}
          accent="bg-[#3A8A98]/10 text-[#255E69]"
          loading={loading}
          to="/admin/clients"
          testId="stat-active-clients"
        />
        <StatCard
          label="Trial-utkast att granska"
          value={data.pendingDrafts}
          icon={Inbox}
          accent="bg-amber-100 text-amber-800"
          loading={loading}
          to="/admin/onboarding-drafts"
          highlight={data.pendingDrafts > 0}
          testId="stat-pending-drafts"
        />
        <StatCard
          label="Optimeringsförslag"
          value={data.pendingSuggestions}
          icon={Wand2}
          accent="bg-violet-100 text-violet-700"
          loading={loading}
          to="/admin/optimering"
          highlight={data.pendingSuggestions > 0}
          testId="stat-pending-suggestions"
        />
        <StatCard
          label="Konversationer senaste 7 dagarna"
          value={data.weekConversations}
          icon={MessageSquare}
          accent="bg-emerald-100 text-emerald-700"
          loading={loading}
          to="/admin/leads"
          testId="stat-week-conversations"
        />
      </div>

      {/* Quick actions */}
      <div className="grid md:grid-cols-3 gap-5">
        <ActionCard
          to="/admin/onboarding-drafts"
          icon={Inbox}
          title="Granska trial-utkast"
          desc="Företag som klarat självbetjäningen och väntar på godkännande för att gå live."
          accentBg="bg-amber-50"
          accentIcon="text-amber-700"
          testId="action-drafts"
        />
        <ActionCard
          to="/admin/optimering"
          icon={Wand2}
          title="Optimera era klienter"
          desc="Låt AI:n läsa senaste veckans konversationer och föreslå nya FAQ:er, kvalificeringsfält och tjänster."
          accentBg="bg-violet-50"
          accentIcon="text-violet-700"
          testId="action-optimering"
        />
        <ActionCard
          to="/skapa-ai-agent"
          icon={Sparkles}
          title="Skapa egen kundmottagare"
          desc="Testa självbetjäningsflödet eller skapa snabbt en ny kundmottagare åt en kund från en URL."
          accentBg="bg-[#3A8A98]/10"
          accentIcon="text-[#255E69]"
          external
          testId="action-create-mottagare"
        />
      </div>

      {/* Two-column: trial drafts + clients with pending suggestions */}
      <div className="grid lg:grid-cols-2 gap-5">
        {/* Trial drafts */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Inbox className="w-4 h-4 text-amber-700" />
              <h2 className="font-semibold text-gray-900">Trial-utkast — senaste</h2>
            </div>
            <Link to="/admin/onboarding-drafts" className="text-xs font-semibold text-[#255E69] hover:text-[#3A8A98]">
              Visa alla →
            </Link>
          </div>
          {loading ? (
            <SkeletonList />
          ) : data.pendingDraftsList.length === 0 ? (
            <EmptyRow text="Inga trial-utkast väntar just nu." />
          ) : (
            <ul className="divide-y divide-gray-100" data-testid="dashboard-drafts-list">
              {data.pendingDraftsList.map((d) => (
                <li key={d.draft_id} className="px-5 py-3 hover:bg-gray-50">
                  <Link to="/admin/onboarding-drafts" className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {d.company_name || d.website_url || "Okänt företag"}
                      </div>
                      <div className="text-xs text-gray-500 truncate flex items-center gap-1.5">
                        <Globe className="w-3 h-3 shrink-0" />
                        {d.website_url || "—"}
                      </div>
                    </div>
                    <span className="text-xs text-gray-400 flex items-center gap-1 shrink-0">
                      <Clock className="w-3 h-3" />
                      {fmtDate(d.updated_at || d.created_at)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Clients with pending optimization suggestions */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Wand2 className="w-4 h-4 text-violet-700" />
              <h2 className="font-semibold text-gray-900">Klienter med öppna förslag</h2>
            </div>
            <Link to="/admin/optimering" className="text-xs font-semibold text-[#255E69] hover:text-[#3A8A98]">
              Öppna optimeringen →
            </Link>
          </div>
          {loading ? (
            <SkeletonList />
          ) : data.topPendingClients.length === 0 ? (
            <EmptyRow text="Inga öppna förslag — generera nya per klient i Optimeringscentralen." />
          ) : (
            <ul className="divide-y divide-gray-100" data-testid="dashboard-pending-clients">
              {data.topPendingClients.map((c) => (
                <li key={c.client_id} className="px-5 py-3 hover:bg-gray-50">
                  <Link to="/admin/optimering" className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{c.name}</div>
                      <div className="text-xs text-gray-500 truncate">
                        {c.industry || "—"} · {c.recent_conversations || 0} konv. senaste 7 dagarna
                      </div>
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5 shrink-0">
                      {c.pending_suggestions} pending
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Footer mini-section: offer shortcut (kept compact) */}
      <div className="grid sm:grid-cols-1 gap-4">
        <Link
          to="/admin/offers"
          className="group bg-white border border-gray-200 rounded-xl px-5 py-4 flex items-center gap-3 hover:border-[#3A8A98] hover:shadow-sm transition-all"
          data-testid="legacy-offers"
        >
          <div className="w-9 h-9 rounded-lg bg-green-50 text-green-700 flex items-center justify-center">
            <FileText className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-gray-900">Offerter</div>
            <div className="text-xs text-gray-500">Skapa och hantera kundoffert-PDF:er</div>
          </div>
          <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-[#255E69]" />
        </Link>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent, loading, to, highlight, testId }) {
  const card = (
    <div
      className={`bg-white rounded-xl border p-5 transition-all ${
        highlight ? "border-amber-200 shadow-sm shadow-amber-100/40" : "border-gray-200"
      } ${to ? "hover:border-[#3A8A98] hover:shadow-md" : ""}`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</p>
          <p className="text-3xl font-bold text-gray-900 mt-1.5">{loading ? "—" : value}</p>
        </div>
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${accent}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
  return to ? <Link to={to}>{card}</Link> : card;
}

function ActionCard({ to, icon: Icon, title, desc, accentBg, accentIcon, external, testId }) {
  const inner = (
    <div className="group bg-white border border-gray-200 rounded-xl p-5 hover:border-[#3A8A98] hover:shadow-md transition-all h-full">
      <div className="flex items-start gap-4">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${accentBg}`}>
          <Icon className={`w-5 h-5 ${accentIcon}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 group-hover:text-[#255E69] transition-colors">{title}</h3>
          <p className="text-sm text-gray-500 mt-1 leading-relaxed">{desc}</p>
        </div>
        <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-[#255E69] transition-colors shrink-0" />
      </div>
    </div>
  );
  if (external) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer" data-testid={testId}>
        {inner}
      </a>
    );
  }
  return (
    <Link to={to} data-testid={testId}>
      {inner}
    </Link>
  );
}

function SkeletonList() {
  return (
    <ul className="divide-y divide-gray-100">
      {[0, 1, 2].map((i) => (
        <li key={i} className="px-5 py-3">
          <div className="h-4 bg-gray-100 rounded w-2/3 animate-pulse" />
          <div className="h-3 bg-gray-100 rounded w-1/3 mt-2 animate-pulse" />
        </li>
      ))}
    </ul>
  );
}

function EmptyRow({ text }) {
  return (
    <div className="px-5 py-10 text-center text-sm text-gray-500">{text}</div>
  );
}
